import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, LogOut, Menu, X, ChevronDown, ShoppingBag, Shield, User } from 'lucide-react';
import MetalButton from './MetalButton';
import logo from '../assets/logo.jpg';
import './Navbar.css';

const Navbar = ({ user, isAdmin, onLoginClick, onSignupClick, onLogout }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setShowUserMenu(false);
    }, [location]);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'Pricing', path: '/pricing' },
        { name: 'Products', path: '/products' },
        { name: 'About', path: '/about' },
        { name: 'Blog', path: '/blog' },
        { name: 'Support', path: '/support' },
    ];

    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }

        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
            <div className="navbar-container">
                <Link to="/" className="navbar-logo">
                    <img src={logo} alt="NetHyTech" className="logo-image" />
                    <span className="logo-text">NetHyTech</span>
                </Link>

                <div className="navbar-links">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                        >
                            {link.name}
                        </Link>
                    ))}
                </div>

                <div className="navbar-actions">
                    {user ? (
                        <div className="user-menu-wrapper">
                            <button
                                className="user-menu-trigger"
                                onClick={() => setShowUserMenu(!showUserMenu)}
                            >
                                <div className="user-avatar">
                                    {user.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="user-name">{user.name.split(' ')[0]}</span>
                                <ChevronDown size={14} />
                            </button>

                            <AnimatePresence>
                                {showUserMenu && (
                                    <motion.div
                                        className="user-dropdown"
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 8 }}
                                    >
                                        <div className="user-dropdown-header">
                                            <span className="user-dropdown-name">{user.name}</span>
                                            <span className="user-dropdown-email">{user.email}</span>
                                        </div>
                                        <div className="user-dropdown-divider" />
                                        <Link
                                            to="/dashboard"
                                            className="user-dropdown-item"
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            <User size={16} />
                                            Profile
                                        </Link>
                                        <Link
                                            to="/my-purchases"
                                            className="user-dropdown-item"
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            <ShoppingBag size={16} />
                                            My Purchases
                                        </Link>
                                        <Link
                                            to="/dashboard"
                                            className="user-dropdown-item"
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            <LayoutDashboard size={16} />
                                            Dashboard
                                        </Link>
                                        {isAdmin && (
                                            <Link
                                                to="/admin"
                                                className="user-dropdown-item"
                                                onClick={() => setShowUserMenu(false)}
                                            >
                                                <Shield size={16} />
                                                Admin
                                            </Link>
                                        )}
                                        <div className="user-dropdown-divider" />
                                        <button className="user-dropdown-item logout" onClick={onLogout}>
                                            <LogOut size={16} />
                                            Logout
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        <>
                            <button className="btn-nav btn-login" onClick={onLoginClick}>
                                Log In
                            </button>
                            <MetalButton className="metal-btn-sm" onClick={onSignupClick}>
                                Get Started
                            </MetalButton>
                        </>
                    )}
                </div>

                <button
                    className="mobile-menu-toggle"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        className="mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="mobile-menu-content">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`mobile-nav-link ${isActive(link.path) ? 'active' : ''}`}
                                >
                                    {link.name}
                                </Link>
                            ))}

                            {user ? (
                                <div className="mobile-user-section">
                                    <div className="mobile-user-info">
                                        <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                                        <span>{user.name}</span>
                                    </div>
                                    <div className="mobile-user-actions">
                                        <Link to="/my-purchases" className="btn-nav btn-login">My Purchases</Link>
                                        <Link to="/dashboard" className="btn-nav btn-login">Profile</Link>
                                        {isAdmin && (
                                            <Link to="/admin" className="btn-nav btn-login">Admin</Link>
                                        )}
                                        <button className="btn-nav btn-logout" onClick={onLogout}>
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="mobile-auth-buttons">
                                    <button className="btn-nav btn-login" onClick={onLoginClick}>Log In</button>
                                    <MetalButton className="metal-btn-sm" onClick={onSignupClick}>Get Started</MetalButton>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
